/**
 * Starting a batch, and looking at what the batches did.
 *
 * Its own file rather than another section of the sending tab: the tab's other
 * panels are settings, and this one performs an act. Mixing "how fast should it
 * go" with "go" is how somebody adjusts a number and presses the wrong thing.
 *
 * THE BUTTON SAYS WHAT IT WILL DO BEFORE IT DOES IT. How many letters, over how
 * long. A batch is irreversible in the only way that matters - a letter cannot
 * be unsent - and the operator's own caps are the answer to "how bad can this
 * be", so they are on the button rather than one tab away.
 *
 * The refusals are named. The server knows in milliseconds whether the campaign
 * is paused, the hour is quiet, the day's cap is spent, test mode is on, no
 * channel is configured, the circuit breaker has tripped or a run is already
 * going; showing "something went wrong" for seven different, fixable situations
 * is the thing this panel exists to avoid.
 *
 * The breaker's own card sits between the button and the test letter, because
 * that is the order the questions come in: may I send, should anyone be sending,
 * and let me look at one first.
 */
import { useCallback, useEffect, useState } from "react";
import { Play, Send, Loader2 } from "lucide-react";
import { Button, Input } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import {
  listCampaignRuns,
  sendTestLetter,
  startCampaignRun,
  type BreakerView,
  type Campaign,
  type MarketingRun,
} from "../../../../lib/marketing";
import { Field, Note, Panel, dt, num } from "../shared";
import { BreakerCard } from "./BreakerCard";

/** Minutes a full batch would take at this campaign's own pace. */
function batchMinutes(count: number, gapSeconds: number): number {
  return Math.max(1, Math.round((Math.max(0, count - 1) * gapSeconds) / 60));
}

export function RunPanel({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const t = useT();
  const [runs, setRuns] = useState<MarketingRun[]>([]);
  const [breaker, setBreaker] = useState<BreakerView | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [started, setStarted] = useState<number | null>(null);
  const [testTo, setTestTo] = useState("");
  const [testNote, setTestNote] = useState<string | null>(null);

  const load = useCallback(() => {
    void listCampaignRuns(campaign.id)
      .then((r) => {
        setRuns(r.runs);
        setBreaker(r.breaker);
      })
      .catch(() => {
        setRuns([]);
        // The breaker is left as it was rather than blanked. A card that
        // disappears on a failed poll reads as "the limit is gone", which is
        // the opposite of what a lost connection means.
      });
  }, [campaign.id]);

  useEffect(load, [load]);

  // A batch runs for minutes after the request has answered, so the list is
  // re-read on a timer while one is open. Polling rather than a socket: this is
  // one operator looking at one page, and a stream would be a second thing to
  // keep alive for a screen that is usually closed.
  const inFlight = runs.some((r) => !r.finishedAt);
  useEffect(() => {
    if (!inFlight) return undefined;
    const id = window.setInterval(load, 10_000);
    return () => window.clearInterval(id);
  }, [inFlight, load]);

  const start = async () => {
    setBusy(true);
    setRefusal(null);
    setStarted(null);
    try {
      const out = await startCampaignRun(campaign.id);
      setStarted(out.budget);
      load();
    } catch (err) {
      const code = (err as { body?: { error?: string } }).body?.error ?? "error";
      setRefusal(code);
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setTestNote(null);
    try {
      const out = await sendTestLetter(campaign.id, { to: testTo.trim() });
      setTestNote(out.redirectedFrom ? t("marketing.run.testRedirected") : t("marketing.run.testSent"));
    } catch (err) {
      const code = (err as { body?: { error?: string } }).body?.error ?? "error";
      setTestNote(t(`marketing.run.stop.${code}`));
    } finally {
      setBusy(false);
    }
  };

  const perBatch = campaign.perRunCap;
  const minutes = batchMinutes(perBatch, campaign.minGapSeconds);

  return (
    <div className="space-y-4">
      <Panel
        title={t("marketing.run.title")}
        description={t("marketing.run.intro", { count: num(perBatch), minutes: num(minutes) })}
        icon={<Play className="size-4" />}
      >
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={start} disabled={!canManage || busy || campaign.status !== "active"}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
            {t("marketing.run.start", { count: num(perBatch) })}
          </Button>
          {campaign.status !== "active" ? (
            <span className="crm-sub">{t("marketing.run.notActive")}</span>
          ) : null}
        </div>

        {started !== null ? (
          <Note tone="info" className="mt-3">
            {t("marketing.run.started", { count: num(started) })}
          </Note>
        ) : null}
        {refusal ? (
          <Note tone="warn" className="mt-3">
            {t(`marketing.run.stop.${refusal}`)}
          </Note>
        ) : null}
      </Panel>

      {breaker ? (
        <BreakerCard campaignId={campaign.id} breaker={breaker} canManage={canManage} onChanged={load} />
      ) : null}

      <Panel
        title={t("marketing.run.testTitle")}
        description={t("marketing.run.testIntro")}
        icon={<Send className="size-4" />}
      >
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t("marketing.run.testTo")} className="min-w-[16rem] flex-1">
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder={t("common.example.yourEmail")} />
          </Field>
          <Button variant="outline" onClick={test} disabled={!canManage || busy || !testTo.includes("@")}>
            {t("marketing.run.testSend")}
          </Button>
        </div>
        {testNote ? <Note tone="info" className="mt-3">{testNote}</Note> : null}
      </Panel>

      <Panel title={t("marketing.run.historyTitle")} description={t("marketing.run.historyIntro")}>
        {runs.length === 0 ? (
          <p className="crm-sub">{t("marketing.run.historyEmpty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="crm-table w-full">
              <thead>
                <tr>
                  <th>{t("marketing.run.colStarted")}</th>
                  <th>{t("marketing.run.colTrigger")}</th>
                  <th className="text-right">{t("marketing.run.colSent")}</th>
                  <th className="text-right">{t("marketing.run.colSkipped")}</th>
                  <th className="text-right">{t("marketing.run.colFailed")}</th>
                  {/* The state sits next to a right-aligned number, so it needs
                      its own gap or the two read as one word. */}
                  <th className="pl-4">{t("marketing.run.colState")}</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id}>
                    <td>{dt(r.startedAt)}</td>
                    <td>{t(`marketing.run.trigger.${r.trigger}`)}</td>
                    <td className="crm-tnum text-right">{num(r.sent)}</td>
                    <td className="crm-tnum text-right">{num(r.skipped)}</td>
                    <td className="crm-tnum text-right">{num(r.failed)}</td>
                    <td className="crm-sub pl-4">
                      {r.error ?? (r.finishedAt ? t("marketing.run.finished") : t("marketing.run.running"))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
